import moment from 'moment';
import type { ConsultantApi, MyConsultationBooking } from './apiTypes';

const CONSULTANT_DATE_FORMATS = ['YYYY-MM-DD', 'DD MMMM, YYYY', 'D MMMM, YYYY', 'DD MMM YYYY', 'D MMM YYYY'] as const;

const CUSTOMER_BLOCKING_STATUSES = new Set(['pending', 'accepted', 'rescheduled']);

export type ConsultationSlotBlockReason = 'customer' | 'consultant';

export type ConsultationSlotOption = {
  time_slot: string;
  bookable: boolean;
  block_reason: ConsultationSlotBlockReason | null;
};

export function parseConsultantYmd(date_str: string | undefined): string | null {
  if (!date_str?.trim()) {
    return null;
  }
  const m = moment(date_str.trim(), [...CONSULTANT_DATE_FORMATS, moment.ISO_8601], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

/** v3: earliest bookable day follows org `day_after` (`package_start_after` from initial API). */
export function resolveV3ConsultationBookingDateRange(
  consultant: ConsultantApi,
  day_after: number,
): { min_ymd: string; max_ymd: string } {
  const days_after = Number.isFinite(day_after) && day_after >= 0 ? day_after : 3;
  const org_min_ymd = moment().add(days_after, 'days').format('YYYY-MM-DD');
  const today_ymd = moment().format('YYYY-MM-DD');
  const consultant_start = parseConsultantYmd(consultant.start_date);
  const consultant_end = parseConsultantYmd(consultant.end_date);

  const consultant_min_ymd = consultant_start ?? org_min_ymd;
  let min_ymd = consultant_min_ymd;
  if (moment(consultant_min_ymd, 'YYYY-MM-DD', true).isBefore(today_ymd)) {
    min_ymd = org_min_ymd;
  }

  let max_ymd = moment().add(1, 'year').format('YYYY-MM-DD');
  if (consultant_end && moment(consultant_end, 'YYYY-MM-DD', true).isValid()) {
    max_ymd = consultant_end;
  }

  if (moment(min_ymd, 'YYYY-MM-DD', true).isAfter(moment(max_ymd, 'YYYY-MM-DD', true))) {
    max_ymd = min_ymd;
  }

  return { min_ymd, max_ymd };
}

function normalizeBookingYmd(raw: unknown): string | null {
  if (raw == null) {
    return null;
  }
  const m = moment(String(raw).trim(), [...CONSULTANT_DATE_FORMATS, moment.ISO_8601], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

function customer_blocked_slots(bookings: MyConsultationBooking[], booking_date: string): Set<string> {
  const blocked = new Set<string>();
  for (const booking of bookings) {
    const ymd = normalizeBookingYmd(booking.date);
    const status = String(booking.status ?? '').toLowerCase().trim();
    if (ymd !== booking_date || !CUSTOMER_BLOCKING_STATUSES.has(status)) {
      continue;
    }
    const slot = String(booking.time_slot ?? '').trim();
    if (slot) {
      blocked.add(slot);
    }
  }
  return blocked;
}

function consultant_blocked_slots(booked_consultations: unknown[] | undefined, booking_date: string): Set<string> {
  const blocked = new Set<string>();
  if (!Array.isArray(booked_consultations)) {
    return blocked;
  }
  for (const item of booked_consultations) {
    if (typeof item === 'string' && item.trim()) {
      blocked.add(item.trim());
      continue;
    }
    if (!item || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const slot = String(row.time_slot ?? row.time ?? '').trim();
    if (!slot) {
      continue;
    }
    const row_date = normalizeBookingYmd(row.date);
    if (!row_date || row_date === booking_date) {
      blocked.add(slot);
    }
  }
  return blocked;
}

export function buildConsultationSlotOptions(
  api_slots: string[] | undefined,
  booking_date: string,
  my_bookings: MyConsultationBooking[],
  booked_consultations: unknown[] | undefined,
): ConsultationSlotOption[] {
  const slots = (api_slots ?? []).map(s => String(s).trim()).filter(Boolean);
  const customer_taken = customer_blocked_slots(my_bookings, booking_date);
  const consultant_taken = consultant_blocked_slots(booked_consultations, booking_date);

  return slots.map(time_slot => {
    if (customer_taken.has(time_slot)) {
      return { time_slot, bookable: false, block_reason: 'customer' };
    }
    if (consultant_taken.has(time_slot)) {
      return { time_slot, bookable: false, block_reason: 'consultant' };
    }
    return { time_slot, bookable: true, block_reason: null };
  });
}

export function countBookableConsultationSlots(options: ConsultationSlotOption[]): number {
  return options.filter(o => o.bookable).length;
}
